import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
// Client-side data layer for orion-web: the harness tool relay. Mirrors the fetch conventions of
// lib/tasks.ts: hit `${API_BASE}/api/...` with credentials. The API routes resolve the caller's metasite
// from their identity.
//
// Design tokens are no longer fetched here — the dashboard iframe renders via the dashboard service's
// `src` render URL, which serves a document that self-contains the metasite's design tokens + runtime
// (resolved server-side from the dashboard service, the single source of truth). See PageSurface.


/** A managed surface's kind — an at-a-glance widget or a drill-in page. Shared with lib/widgets-folder. */
export type WidgetKind = 'widget' | 'page';

// Session-scoped cache for widget tool-call results — a reload of the same dashboard otherwise refetches
// every widget's data byte-identical (the render-doc + every /api/tool-call response have zero client
// caching today). Every call reaching this function is safe to cache: the harness's bridge is deny-by-
// default for a widget caller (`isBridgeAllowed` in orion-harness-poc/src/endpoints/tool-call.ts) — a
// widget can only reach read-only tools plus the three data-path execute tools (ExecuteWixAPI,
// ComposioExecuteTool, McpExecuteTool), and those three are gated the same way real writes are (the
// server-side denial is what actually keeps a widget from mutating, not anything client-side); there is
// no call this function can make that the server would have treated as a write. In-memory (module-level
// Map), not sessionStorage — this only needs to live as long as the tab does.
const MAX_CACHE_ENTRIES = 64;
interface CacheEntry {
  result: unknown;
}
const toolCallCache = new Map<string, CacheEntry>();

/** Sorted-key JSON so `{a:1,b:2}` and `{b:2,a:1}` hash identically. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`).join(',')}}`;
}

function cacheKeyFor(tool: string, params: unknown): string {
  return `${tool}:${stableStringify(params ?? {})}`;
}

/** LRU touch: re-inserting a key moves it to the end of the Map's iteration order (most-recently-used). */
function touchCache(key: string, entry: CacheEntry): void {
  toolCallCache.delete(key);
  toolCallCache.set(key, entry);
  while (toolCallCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = toolCallCache.keys().next().value;
    if (oldestKey === undefined) break;
    toolCallCache.delete(oldestKey);
  }
}

async function fetchToolResult(tool: string, params: unknown): Promise<unknown> {
  const res = await apiFetch(`${API_BASE}/api/tool-call`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tool, params: params ?? {} }),
  });
  const data = (await res.json().catch(() => ({}))) as { result?: unknown; error?: string; body?: string };
  if (!res.ok) {
    throw new Error(toolCallErrorMessage(data, res.status));
  }
  return data.result;
}

/**
 * Relay one `orion.call(tool, params)` to the harness. Resolves with the tool's result.
 *
 * Stale-while-revalidate: a cache hit resolves IMMEDIATELY with the last-known value, and a fresh fetch
 * fires in the background (unawaited) to refresh the cache for next time — so a widget that already
 * rendered once elsewhere in the tab paints instantly instead of waiting on the network again. A cache
 * miss awaits the real call as before. Errors are never cached — a failed call always hits the network
 * again next time, so a transient failure can't wedge a widget in a broken state for the rest of the tab.
 */
export async function callWidgetTool(tool: string, params: unknown): Promise<unknown> {
  const key = cacheKeyFor(tool, params);
  const cached = toolCallCache.get(key);

  if (cached) {
    touchCache(key, cached);
    // Background refresh — deliberately not awaited/returned. A failed revalidation just leaves the
    // existing cache entry in place for the next read; it must never surface to THIS caller, which
    // already resolved off the cache.
    void fetchToolResult(tool, params)
      .then((result) => touchCache(key, { result }))
      .catch(() => {});
    return cached.result;
  }

  const result = await fetchToolResult(tool, params);
  touchCache(key, { result });
  return result;
}

/** Extract the most actionable message from a failed /api/tool-call response. On an upstream failure the
 *  route wraps the harness's own error as `{ error: 'upstream_error', body: '<raw upstream body>' }`, so the
 *  useful detail (e.g. "unknown tool: CallWixSiteAPI") lives in `body`, not in the generic `error` code.
 *  Prefer the upstream body's own error/message, fall back to its raw text, then the wrapper code / status. */
function toolCallErrorMessage(data: { error?: string; body?: string }, status: number): string {
  if (data.body) {
    try {
      const inner = JSON.parse(data.body) as { error?: string; message?: string };
      const innerMsg = inner.error || inner.message;
      if (innerMsg) {
        return innerMsg;
      }
    } catch {
      // Non-JSON body — surface the raw text below.
    }
    return data.body;
  }
  return data.error || `HTTP ${status}`;
}
